import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import type { DashboardDataGridField } from "~/feature/dashboard/components/DashboardDataGrid/types";
import { setHiddenField } from "~/utils/dashboardView/columnVisibility";
import {
  type DashboardDensity,
  DEFAULT_DASHBOARD_DENSITY,
  DEFAULT_HIDDEN_DASHBOARD_FIELDS,
  DEFAULT_RELIABLE_PRICES_ONLY,
} from "~/utils/dashboardView/viewPanelConfig";

interface IDashboardViewContext {
  hiddenColumns: ReadonlySet<DashboardDataGridField>;
  setColumnVisible: (field: DashboardDataGridField, visible: boolean) => void;
  density: DashboardDensity;
  setDensity: (density: DashboardDensity) => void;
  reliablePricesOnly: boolean;
  setReliablePricesOnly: (reliablePricesOnly: boolean) => void;
}

const DashboardViewContext = createContext<IDashboardViewContext | null>(null);

interface IDashboardViewProviderProps {
  children: ReactNode;
}

export const DashboardViewProvider = ({
  children,
}: IDashboardViewProviderProps) => {
  const [hiddenColumns, setHiddenColumns] = useState<
    ReadonlySet<DashboardDataGridField>
  >(DEFAULT_HIDDEN_DASHBOARD_FIELDS);
  const [density, setDensity] = useState<DashboardDensity>(
    DEFAULT_DASHBOARD_DENSITY
  );
  const [reliablePricesOnly, setReliablePricesOnly] = useState(
    DEFAULT_RELIABLE_PRICES_ONLY
  );

  const setColumnVisible = useCallback(
    (field: DashboardDataGridField, visible: boolean) =>
      setHiddenColumns((prev) => setHiddenField(prev, field, visible)),
    []
  );

  const value = useMemo(
    () => ({
      hiddenColumns,
      setColumnVisible,
      density,
      setDensity,
      reliablePricesOnly,
      setReliablePricesOnly,
    }),
    [hiddenColumns, setColumnVisible, density, reliablePricesOnly]
  );

  return (
    <DashboardViewContext.Provider value={value}>
      {children}
    </DashboardViewContext.Provider>
  );
};

export const useDashboardView = (): IDashboardViewContext => {
  const context = useContext(DashboardViewContext);

  if (!context) {
    throw new Error(
      "useDashboardView must be used within DashboardViewProvider"
    );
  }

  return context;
};
